import {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {Check, ChevronDown, Phone} from 'lucide-react';
import type {RootState} from '../../../store/store';
import {selectUserId} from '../../../store/features/Auth/authSlice';
import {setSelectedPhoneNumber} from '../../../store/features/calls/dialerSlice';
import {useFetchMsisdnsListQuery} from '../../../store/features/MsisdnSettings/msisdnSettingsService';
import type {Msisdn} from '../../../store/features/Msisdns/msisdnService';
import {getFormatedPhoneNumber} from '../../../utilis/msisdnUtilis';
import {dialerMessages as m} from './dialerMessages';

const lineText = (line: Pick<Msisdn, 'label' | 'msisdn'>): string => `${line.label} · ${getFormatedPhoneNumber(line.msisdn, false)}`;

export const LinePicker = () => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const userId = useSelector(selectUserId);
    const selected = useSelector((state: RootState) => state.dialer.selectedPhoneNumber) as Msisdn | undefined;
    const {data} = useFetchMsisdnsListQuery({userId});
    const lines = (data?.data ?? []) as Msisdn[];
    const [open, setOpen] = useState(false);
    const container = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        const onPointerDown = (event: PointerEvent) => {
            if (!container.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [open]);

    if (lines.length === 0 || !selected) {
        return null;
    }

    return (
        <div ref={container} className="relative">
            <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={intl.formatMessage(m.callFrom, {label: selected.label})}
                data-testid="dialer-line-picker"
                onClick={() => setOpen(current => !current)}
                className="phq-call-press flex h-9 w-full items-center gap-2 rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3 text-left text-sm font-semibold"
            >
                <Phone size={16} className="shrink-0 text-[var(--phq-call-ink-secondary)]" aria-hidden="true"/>
                <span className="min-w-0 flex-1 truncate">{lineText(selected)}</span>
                <ChevronDown size={16} className="shrink-0 text-[var(--phq-call-ink-muted)]" aria-hidden="true"/>
            </button>
            {open && (
                <div
                    role="listbox"
                    aria-label={intl.formatMessage(m.lines)}
                    className="phq-call-swap absolute left-0 right-0 top-[calc(100%+4px)] z-10 flex max-h-64 flex-col gap-0.5 overflow-y-auto rounded-xl border border-[var(--phq-call-line)] bg-[var(--phq-call-surface)] p-1 phq-call-shadow-overlay"
                >
                    {lines.map(line => {
                        const active = line.msisdnId === selected.msisdnId;
                        return (
                            <button
                                key={line.msisdnId}
                                type="button"
                                role="option"
                                aria-selected={active}
                                onClick={() => {
                                    dispatch(setSelectedPhoneNumber(line));
                                    setOpen(false);
                                }}
                                className={`flex h-10 items-center gap-2 rounded-lg px-2.5 text-left text-sm ${active ? 'bg-[var(--phq-call-brand-tint-subtle)] font-semibold' : 'font-medium hover:bg-[var(--phq-call-surface-muted)]'}`}
                            >
                                <span className="min-w-0 flex-1 truncate">{lineText(line)}</span>
                                {active && <Check size={16} className="text-[var(--phq-call-brand)]" aria-hidden="true"/>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
